// =====================================================================================
// FILE: src/services/iptvOptions.js
// =====================================================================================

const AIRTABLE_TOKEN = process.env.REACT_APP_AIRTABLE_TOKEN;
const BASE_ID = process.env.REACT_APP_AIRTABLE_BASE_ID;
const IPTV_TABLE_ID = process.env.REACT_APP_AIRTABLE_IPTV_TABLE_ID;

const CACHE_KEY = 'venueflow_iptv_options';

// Fallback options in case metadata fetch fails
const FALLBACK_OPTIONS = {
  timing: [
    "Pre-Match",
    "During ALL Match",
    "During 1st Quarter Time",
    "During Half Time",
    "During Three Quarter Time",
    "Post-Match",
    "Post Match",
    "Special Gates"
  ],
  location: [
    "Premium Dining",
    "Corporate Boxes",
    "General Admission",
    "Harrison Room",
    "Ryder Room"
  ]
};

/**
 * Get cached IPTV options from sessionStorage
 */
function getCachedOptions() {
  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (cached) {
      return JSON.parse(cached);
    }
  } catch (e) {
    console.error('Failed to read cached IPTV options:', e);
  }
  return null;
}

/**
 * Save IPTV options to sessionStorage
 */
function setCachedOptions(options) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(options));
  } catch (e) {
    console.error('Failed to cache IPTV options:', e);
  }
}

/**
 * Fetches IPTV field options (Select Timing and Select Location) from Airtable Metadata API
 * Returns cached options immediately if available, then updates in background
 * @returns {Promise<Object>} Object with timing and location arrays
 */
export async function fetchIptvOptions() {
  // Return cached options immediately if available
  const cached = getCachedOptions();
  if (cached) {
    // Fetch fresh data in background to update cache
    fetchFreshIptvOptions();
    return cached;
  }

  // No cache - fetch and wait
  return fetchFreshIptvOptions();
}

/**
 * Fetches fresh IPTV options from Airtable Metadata API and updates cache
 */
async function fetchFreshIptvOptions() {
  try {
    const response = await fetch(
      `https://api.airtable.com/v0/meta/bases/${BASE_ID}/tables`,
      {
        headers: {
          Authorization: `Bearer ${AIRTABLE_TOKEN}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Metadata API error: ${response.status}`);
    }

    const data = await response.json();
    
    // Find the IPTV table
    const iptvTable = data.tables.find(table => table.id === IPTV_TABLE_ID);
    
    if (!iptvTable) {
      console.warn('IPTV table not found in metadata, using fallback options');
      return FALLBACK_OPTIONS;
    }

    // Extract options from the Select Timing field
    const timingField = iptvTable.fields.find(field => 
      field.name === 'Select Timing' || field.name === 'selectTiming'
    );
    
    // Extract options from the Select Location field
    const locationField = iptvTable.fields.find(field => 
      field.name === 'Select Location' || field.name === 'selectLocation'
    );

    const options = {
      timing: timingField?.options?.choices?.map(choice => choice.name) || FALLBACK_OPTIONS.timing,
      location: locationField?.options?.choices?.map(choice => choice.name) || FALLBACK_OPTIONS.location,
    };

    // Cache the results
    setCachedOptions(options);
    
    return options;
  } catch (error) {
    console.error('Failed to fetch IPTV options from metadata:', error);
    // Return fallback options on error
    return FALLBACK_OPTIONS;
  }
}

/**
 * Clears the cached IPTV options (useful for testing or forcing refresh)
 */
export function clearIptvOptionsCache() {
  try {
    sessionStorage.removeItem(CACHE_KEY);
  } catch (e) {
    console.error('Failed to clear IPTV options cache:', e);
  }
}
